import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createInitiative,
  updateInitiative,
} from "../../../apis/initiatives.api";
import { SaveAndCancelButton } from "../../../components/buttons";
import FlexBox from "../../../components/layouts/flexbox";
import { InitiativeForm } from "./form";
import {
  buildInitiativePayload,
  DEFAULT_INITIATIVE_FORM_VALUES,
  initiativeToFormValues,
  type InitiativeFormErrors,
  type InitiativeFormValues,
  validateInitiativeForm,
} from "./form-config";

const DRAWER_DESCRIPTION =
  "Share the initiatives planned over the next 12 months so we can match with relevant vendors";

type AddOrUpdateInitiativeDrawerProps = {
  open: boolean;
  onClose: () => void;
  initiative?: any | null;
  onSuccess?: () => void;
};

function AddOrUpdateInitiativeDrawer({
  open,
  onClose,
  initiative = null,
  onSuccess,
}: AddOrUpdateInitiativeDrawerProps) {
  const dispatch = useDispatch();
  const isEdit = Boolean(initiative?.id);
  const [values, setValues] = useState<InitiativeFormValues>(
    DEFAULT_INITIATIVE_FORM_VALUES
  );
  const [errors, setErrors] = useState<InitiativeFormErrors>({});
  const [selectedAmbassador, setSelectedAmbassador] = useState<{
    id: string;
    fullName: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (initiative) {
      setValues(initiativeToFormValues(initiative));
      setSelectedAmbassador(
        initiative.ambassadorId
          ? {
              id: initiative.ambassadorId,
              fullName: initiative.ambassadorId,
            }
          : null
      );
    } else {
      setValues(DEFAULT_INITIATIVE_FORM_VALUES);
      setSelectedAmbassador(null);
    }
    setErrors({});
  }, [open, initiative]);

  const handleChange = (field: keyof InitiativeFormValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleClose = () => {
    setValues(DEFAULT_INITIATIVE_FORM_VALUES);
    setSelectedAmbassador(null);
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    const nextErrors = validateInitiativeForm(values, {
      requireAmbassador: !isEdit,
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    const payload = buildInitiativePayload(values, {
      includeAmbassadorId: !isEdit,
    });

    const response: any = await dispatch(
      (isEdit
        ? updateInitiative(initiative.id, payload as any)
        : createInitiative(payload as any)) as any
    );
    setSubmitting(false);

    if (response?.success) {
      onSuccess?.();
      handleClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <FlexBox
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0,
        }}
      >
        <Stack gap={0.5} pr={1}>
          <Typography fontSize={18} fontWeight={700} color="#0F172A">
            {isEdit ? "Edit Initiative" : "Create Initiative"}
          </Typography>
          <Typography fontSize={13} color="#64748B">
            {DRAWER_DESCRIPTION}
          </Typography>
        </Stack>
        <IconButton onClick={handleClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </FlexBox>

      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2.5 }}>
        <InitiativeForm
          values={values}
          errors={errors}
          onChange={handleChange}
          showAmbassadorSelect={!isEdit}
          selectedAmbassador={selectedAmbassador}
          onAmbassadorChange={setSelectedAmbassador}
        />
      </Box>

      <FlexBox
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid #E5E7EB",
          flexShrink: 0,
          backgroundColor: "#FAFAFA",
        }}
      >
        <SaveAndCancelButton
          primaryText={isEdit ? "Save Changes" : "Create Initiative"}
          defaultText="Discard Changes"
          onPrimaryActionClick={handleSubmit}
          onDefaultButtonClick={handleClose}
          isPrimaryButtonDisabled={submitting}
        />
      </FlexBox>
    </Drawer>
  );
}

export { AddOrUpdateInitiativeDrawer };
